import { motion } from "framer-motion";
import { Monitor, Cpu, Rocket, CheckCircle2 } from "lucide-react";

export function HeroGraphic() {
  return (
    <div className="relative w-full max-w-[540px] mx-auto aspect-square">
      <svg
        viewBox="0 0 500 500"
        fill="none"
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="hg-amber" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(30 95% 55%)" />
            <stop offset="100%" stopColor="hsl(45 100% 60%)" />
          </linearGradient>
          <linearGradient id="hg-amber-fade" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="hsl(30 95% 55%)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="hsl(30 95% 55%)" stopOpacity="0" />
          </linearGradient>
          <filter id="hg-glow">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="hg-soft-glow">
            <feGaussianBlur stdDeviation="15" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {Array.from({ length: 11 }, (_, r) =>
          Array.from({ length: 11 }, (_, c) => (
            <circle
              key={`d-${r}-${c}`}
              cx={25 + c * 45}
              cy={25 + r * 45}
              r="0.8"
              fill="hsl(30 95% 55%)"
              opacity="0.07"
            />
          ))
        )}

        <polygon
          points="250,55 405,145 405,325 250,415 95,325 95,145"
          stroke="url(#hg-amber)"
          strokeWidth="0.8"
          fill="none"
          opacity="0.12"
        />

        <polygon
          points="250,105 365,160 365,310 250,365 135,310 135,160"
          stroke="url(#hg-amber)"
          strokeWidth="0.5"
          fill="hsl(30 95% 55%)"
          fillOpacity="0.02"
          opacity="0.15"
        />

        <ellipse
          cx="250"
          cy="235"
          rx="200"
          ry="140"
          stroke="hsl(30 95% 55%)"
          strokeWidth="0.6"
          fill="none"
          opacity="0.08"
          strokeDasharray="6 10"
        />

        <ellipse
          cx="250"
          cy="235"
          rx="155"
          ry="95"
          stroke="hsl(45 100% 60%)"
          strokeWidth="0.4"
          fill="none"
          opacity="0.06"
          strokeDasharray="4 8"
        />

        <circle
          cx="250"
          cy="220"
          r="65"
          fill="hsl(30 95% 55%)"
          opacity="0.04"
          filter="url(#hg-soft-glow)"
        />

        <g transform="translate(215, 175)" filter="url(#hg-glow)">
          <path
            d="M35 0 L70 18 L70 48 C70 66 53 78 35 86 C17 78 0 66 0 48 L0 18Z"
            stroke="url(#hg-amber)"
            strokeWidth="1.5"
            fill="hsl(30 95% 55%)"
            fillOpacity="0.08"
          />
          <path
            d="M25 43 L32 50 L48 33"
            stroke="hsl(45 100% 60%)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </g>

        <line x1="290" y1="185" x2="380" y2="100" stroke="hsl(30 95% 55%)" strokeWidth="0.5" opacity="0.12" strokeDasharray="4 6" />
        <line x1="215" y1="240" x2="110" y2="320" stroke="hsl(30 95% 55%)" strokeWidth="0.5" opacity="0.1" strokeDasharray="4 6" />
        <line x1="285" y1="260" x2="375" y2="355" stroke="hsl(45 100% 60%)" strokeWidth="0.5" opacity="0.08" strokeDasharray="4 6" />

        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 250 235"
            to="360 250 235"
            dur="35s"
            repeatCount="indefinite"
          />
          <circle cx="450" cy="235" r="3" fill="hsl(30 95% 55%)" opacity="0.35" filter="url(#hg-glow)" />
        </g>

        <g>
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="360 250 235"
            to="0 250 235"
            dur="28s"
            repeatCount="indefinite"
          />
          <circle cx="405" cy="235" r="2.5" fill="hsl(45 100% 60%)" opacity="0.25" filter="url(#hg-glow)" />
        </g>

        <circle cx="160" cy="120" r="2" fill="hsl(30 95% 55%)" opacity="0.25" className="hero-node-pulse" />
        <circle cx="340" cy="130" r="1.5" fill="hsl(45 100% 60%)" opacity="0.2" className="hero-node-pulse" style={{ animationDelay: "1s" }} />
        <circle cx="110" cy="290" r="1.5" fill="hsl(30 95% 55%)" opacity="0.2" className="hero-node-pulse" style={{ animationDelay: "0.5s" }} />
        <circle cx="390" cy="310" r="2" fill="hsl(45 100% 60%)" opacity="0.15" className="hero-node-pulse" style={{ animationDelay: "1.5s" }} />
        <circle cx="250" cy="430" r="1.5" fill="hsl(30 95% 55%)" opacity="0.18" className="hero-node-pulse" style={{ animationDelay: "2s" }} />
        <circle cx="420" cy="200" r="1" fill="hsl(30 95% 55%)" opacity="0.15" className="hero-node-pulse" style={{ animationDelay: "0.8s" }} />
        <circle cx="80" cy="190" r="1" fill="hsl(45 100% 60%)" opacity="0.12" className="hero-node-pulse" style={{ animationDelay: "1.2s" }} />
      </svg>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="absolute top-[8%] right-[2%] hero-float-a"
      >
        <div className="glass-card rounded-xl p-3 pr-5 border-gradient">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 rounded-md bg-primary/15 flex items-center justify-center">
              <Monitor className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-[11px] text-muted-foreground font-medium">
              Desktop Client
            </span>
          </div>
          <span className="text-xl font-bold text-gradient leading-none">
            v1.0
          </span>
          <div className="flex items-end gap-[3px] mt-2">
            {[40, 55, 72, 62, 48, 35, 26].map((h, i) => (
              <div
                key={i}
                className="w-[5px] rounded-sm bg-primary/30"
                style={{ height: `${h * 0.22}px` }}
              />
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0, duration: 0.6 }}
        className="absolute bottom-[25%] left-[0%] hero-float-b"
      >
        <div className="glass-card rounded-xl p-3 pr-5 border-gradient">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 rounded-md bg-primary/15 flex items-center justify-center">
              <Cpu className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-[11px] text-muted-foreground font-medium">
              AI Features
            </span>
          </div>
          <span className="text-xl font-bold text-gradient leading-none">
            Live
          </span>
          <div className="w-full h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
            <div className="h-full w-[92%] rounded-full bg-gradient-to-r from-primary to-accent" />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-[8%] right-[8%] hero-float-c"
      >
        <div className="glass-card rounded-xl p-3 pr-5 border-gradient">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 rounded-md bg-primary/15 flex items-center justify-center">
              <Rocket className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-[11px] text-muted-foreground font-medium">
              Ship Status
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-gradient leading-none">
              Ready
            </span>
            <span className="text-[10px] text-muted-foreground">to release</span>
          </div>
          <div className="flex gap-1.5 mt-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-4 h-4 rounded-full bg-primary/15 flex items-center justify-center"
              >
                <CheckCircle2 className="w-2.5 h-2.5 text-primary" />
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
